import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import SaveIcon from "@material-ui/icons/Save";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TextMeBotApiKeySelector from "../../components/TextMeBotApiKeySelector";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  buttonItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  uploadInput: {
    display: "none",
  },
  mediaPreview: {
    maxWidth: "200px",
    maxHeight: "200px",
    marginTop: theme.spacing(1),
  },
}));

const ButtonsListsCampaignForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = React.useState(false);
  const [campaignName, setCampaignName] = React.useState("");
  const [type, setType] = React.useState("buttons");
  const [textMeBotApiKeyId, setTextMeBotApiKeyId] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [mediaUrl, setMediaUrl] = React.useState("");
  const [mediaFile, setMediaFile] = React.useState(null);
  const [buttons, setButtons] = React.useState([{ text: "" }]);
  const [listSections, setListSections] = React.useState([
    { title: "", rows: [{ title: "", description: "" }] }
  ]);
  const [recipientInput, setRecipientInput] = React.useState("");
  const [recipients, setRecipients] = React.useState([]);

  const handleAddButton = () => {
    if (buttons.length < 3) {
      setButtons([...buttons, { text: "" }]);
    }
  };

  const handleRemoveButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handleButtonChange = (index, value) => {
    const newButtons = [...buttons];
    newButtons[index].text = value;
    setButtons(newButtons);
  };

  const handleAddSection = () => {
    if (listSections.length < 10) {
      setListSections([
        ...listSections,
        { title: "", rows: [{ title: "", description: "" }] }
      ]);
    }
  };

  const handleRemoveSection = (index) => {
    setListSections(listSections.filter((_, i) => i !== index));
  };

  const handleSectionChange = (index, field, value) => {
    const newSections = [...listSections];
    newSections[index][field] = value;
    setListSections(newSections);
  };

  const handleAddRow = (sectionIndex) => {
    const newSections = [...listSections];
    if (newSections[sectionIndex].rows.length < 10) {
      newSections[sectionIndex].rows.push({ title: "", description: "" });
      setListSections(newSections);
    }
  };

  const handleRemoveRow = (sectionIndex, rowIndex) => {
    const newSections = [...listSections];
    newSections[sectionIndex].rows = newSections[sectionIndex].rows.filter(
      (_, i) => i !== rowIndex
    );
    setListSections(newSections);
  };

  const handleRowChange = (sectionIndex, rowIndex, field, value) => {
    const newSections = [...listSections];
    newSections[sectionIndex].rows[rowIndex][field] = value;
    setListSections(newSections);
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (16MB)
    if (file.size > 16 * 1024 * 1024) {
      toast.error("El archivo no debe superar 16MB");
      return;
    }

    setMediaFile(file);
    setMediaUrl(URL.createObjectURL(file));
  };

  const handleAddRecipient = () => {
    if (!recipientInput.trim()) return;

    const lines = recipientInput.split("\n").filter(line => line.trim());
    const newRecipients = lines.map(line => {
      const parts = line.split(",");
      return {
        phone: parts[0].trim(),
        name: parts[1]?.trim() || ""
      };
    });

    setRecipients([...recipients, ...newRecipients]);
    setRecipientInput("");
  };

  const handleRemoveRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate APIKey is selected
      if (!textMeBotApiKeyId) {
        toast.error("Por favor seleccione una APIKey de TextMeBot");
        setLoading(false);
        return;
      }

      // Prepare recipients in the correct format
      const formattedRecipients = recipients.map(r => ({
        phoneNumber: r.phone,
        name: r.name || ""
      }));

      const payload = {
        name: campaignName,
        type: type,
        textMeBotApiKeyId: parseInt(textMeBotApiKeyId),
        messageText: message,
        recipients: formattedRecipients
      };

      if (type === "buttons") {
        // Filter out empty buttons and format with id
        payload.buttonsConfig = buttons
          .filter(b => b.text && b.text.trim())
          .map((b, index) => ({
            id: `btn_${index + 1}`,
            text: b.text.trim()
          }));
      } else {
        // Format list config
        payload.listConfig = {
          title: "Seleccione una opción",
          buttonText: "Ver opciones",
          sections: listSections
            .filter(s => s.title && s.rows.some(r => r.title))
            .map((s, sIndex) => ({
              title: s.title,
              rows: s.rows
                .filter(r => r.title && r.title.trim())
                .map((r, rIndex) => ({
                  id: `row_${sIndex}_${rIndex}`,
                  title: r.title.trim(),
                  description: r.description?.trim() || ""
                }))
            }))
        };
      }

      // Handle media upload first if present
      if (mediaFile) {
        const mediaFormData = new FormData();
        mediaFormData.append("media", mediaFile);

        const { data: mediaData } = await api.post(
          "/campaigns/buttons-lists/upload-media",
          mediaFormData,
          {
            headers: { "Content-Type": "multipart/form-data" }
          }
        );

        payload.mediaUrl = mediaData.mediaUrl;
        payload.mediaType = mediaData.mediaType;
      }

      if (isEditing) {
        await api.put(`/campaigns/buttons-lists/${id}`, payload);
        toast.success("Campaña actualizada exitosamente");
      } else {
        await api.post("/campaigns/buttons-lists", payload);
        toast.success("Campaña creada exitosamente");
      }
      history.push("/campaigns/buttons-lists");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push("/campaigns/buttons-lists");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          {isEditing ? "Editar Campaña" : "Nueva Campaña de Botones y Listas"}
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<ArrowBackIcon />}
          >
            Volver
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <form onSubmit={handleFormSubmit}>
          {/* Información Básica */}
          <div className={classes.section}>
            <Typography variant="h6" gutterBottom>
              Información Básica
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre de la Campaña"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="buttons">Botones</MenuItem>
                    <MenuItem value="list">Lista</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextMeBotApiKeySelector
                  value={textMeBotApiKeyId}
                  onChange={setTextMeBotApiKeyId}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Mensaje"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  disabled={loading}
                  helperText="Texto del mensaje que se enviará"
                />
              </Grid>
            </Grid>
          </div>

          {/* Multimedia */}
          <div className={classes.section}>
            <Typography variant="h6" gutterBottom>
              Multimedia (Opcional)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <input
                  accept="image/*,video/*,audio/*,.pdf"
                  className={classes.uploadInput}
                  id="media-upload"
                  type="file"
                  onChange={handleMediaUpload}
                  disabled={loading}
                />
                <label htmlFor="media-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={loading}
                  >
                    Subir Archivo (Máx 16MB)
                  </Button>
                </label>
                {mediaUrl && (
                  <div>
                    {mediaFile?.type.startsWith("image/") && (
                      <img
                        src={mediaUrl}
                        alt="Preview"
                        className={classes.mediaPreview}
                      />
                    )}
                    {mediaFile?.type.startsWith("video/") && (
                      <video
                        src={mediaUrl}
                        controls
                        className={classes.mediaPreview}
                      />
                    )}
                    {mediaFile && (
                      <Typography variant="caption" display="block">
                        {mediaFile.name} ({(mediaFile.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                    )}
                  </div>
                )}
              </Grid>
            </Grid>
          </div>

          {/* Botones o Listas */}
          {type === "buttons" ? (
            <div className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Botones (Máximo 3, 20 caracteres cada uno)
              </Typography>
              {buttons.map((button, index) => (
                <div key={index} className={classes.buttonItem}>
                  <TextField
                    fullWidth
                    label={`Botón ${index + 1}`}
                    value={button.text}
                    onChange={(e) => handleButtonChange(index, e.target.value)}
                    inputProps={{ maxLength: 20 }}
                    disabled={loading}
                  />
                  {buttons.length > 1 && (
                    <IconButton
                      onClick={() => handleRemoveButton(index)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </div>
              ))}
              {buttons.length < 3 && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddButton}
                  disabled={loading}
                >
                  Agregar Botón
                </Button>
              )}
            </div>
          ) : (
            <div className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Secciones de Lista (Máximo 10 secciones, 10 opciones cada una)
              </Typography>
              {listSections.map((section, sectionIndex) => (
                <Paper
                  key={sectionIndex}
                  style={{ padding: 16, marginBottom: 16 }}
                  variant="outlined"
                >
                  <Grid container spacing={2}>
                    <Grid item xs={11}>
                      <TextField
                        fullWidth
                        label={`Título de Sección ${sectionIndex + 1}`}
                        value={section.title}
                        onChange={(e) =>
                          handleSectionChange(sectionIndex, "title", e.target.value)
                        }
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      {listSections.length > 1 && (
                        <IconButton
                          onClick={() => handleRemoveSection(sectionIndex)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Grid>
                    {section.rows.map((row, rowIndex) => (
                      <React.Fragment key={rowIndex}>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label="Título"
                            value={row.title}
                            onChange={(e) =>
                              handleRowChange(
                                sectionIndex,
                                rowIndex,
                                "title",
                                e.target.value
                              )
                            }
                            disabled={loading}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Descripción"
                            value={row.description}
                            onChange={(e) =>
                              handleRowChange(
                                sectionIndex,
                                rowIndex,
                                "description",
                                e.target.value
                              )
                            }
                            disabled={loading}
                          />
                        </Grid>
                        <Grid item xs={1}>
                          {section.rows.length > 1 && (
                            <IconButton
                              onClick={() => handleRemoveRow(sectionIndex, rowIndex)}
                              disabled={loading}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Grid>
                      </React.Fragment>
                    ))}
                    {section.rows.length < 10 && (
                      <Grid item xs={12}>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddRow(sectionIndex)}
                          disabled={loading}
                        >
                          Agregar Opción
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}
              {listSections.length < 10 && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddSection}
                  disabled={loading}
                >
                  Agregar Sección
                </Button>
              )}
            </div>
          )}

          {/* Destinatarios */}
          <div className={classes.section}>
            <Typography variant="h6" gutterBottom>
              Destinatarios
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Agregar Destinatarios"
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  disabled={loading}
                  helperText="Un número por línea. Formato: +5491112345678 o +5491112345678,Juan Pérez"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddRecipient}
                  disabled={loading || !recipientInput.trim()}
                >
                  Agregar Destinatarios
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Destinatarios agregados: {recipients.length}
                </Typography>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {recipients.map((recipient, index) => (
                    <Chip
                      key={index}
                      label={`${recipient.phone}${recipient.name ? ` - ${recipient.name}` : ""}`}
                      onDelete={() => handleRemoveRecipient(index)}
                      disabled={loading}
                    />
                  ))}
                </div>
              </Grid>
            </Grid>
          </div>

          <div style={{ marginTop: 24, display: "flex", gap: 16 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                loading || 
                recipients.length === 0 || 
                !campaignName || 
                !message || 
                !textMeBotApiKeyId
              }
              startIcon={<SaveIcon />}
            >
              {isEditing ? "Actualizar" : "Crear"} Campaña
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Paper>
    </MainContainer>
  );
};

export default ButtonsListsCampaignForm;
